import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Partner } from "@shared/schema";

interface PartnerSelectProps {
  value?: string | null;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

export function PartnerSelect({
  value,
  onValueChange,
  placeholder = "Select referring partner",
  disabled = false,
  "data-testid": testId,
}: PartnerSelectProps) {
  const [, navigate] = useLocation();
  const { data: partners, isLoading } = useQuery<Partner[]>({
    queryKey: ["/api/crm/partners", { isActive: "true" }],
  });

  const hasPartners = partners && partners.length > 0;

  return (
    <Select
      value={value || undefined}
      onValueChange={onValueChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={isLoading ? "Loading..." : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {hasPartners ? (
          partners.map((partner) => (
            <SelectItem key={partner.id} value={partner.id}>
              {partner.partnerName}
              {partner.companyName && ` (${partner.companyName})`}
            </SelectItem>
          ))
        ) : (
          <div
            className="py-4 px-3 text-center"
            data-testid="partner-select-empty-state"
          >
            <p className="text-sm text-muted-foreground mb-2">
              No partners found
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
              data-testid="partner-select-create-link"
              onPointerDown={(e) => {
                e.preventDefault();
                navigate("/partners");
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Create a partner
            </button>
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
